import { useEffect } from "react";
import { Outlet } from "react-router";
import { toast } from "sonner";
import { Sidebar } from "./Sidebar.tsx";
import { PlayerBar } from "@web/features/player/PlayerBar.tsx";
import { DetailSheet } from "./DetailSheet.tsx";
import { LibraryDndProvider } from "./libraryDnd.tsx";
import { ExploreDrawer } from "@web/features/browse/ExploreDrawer.tsx";
import { ConnectionBanner } from "@web/features/diagnostics/ConnectionBanner.tsx";
import { useStartSync } from "@web/features/library/api.ts";
import { useUiStore } from "@web/stores/ui.ts";
import { useSelectionStore } from "@web/stores/selection.ts";
import { useBrowseTrailStore } from "@web/stores/browseTrail.ts";
import { useDetailStore } from "@web/stores/detail.ts";
import { useRedo, useUndo } from "@web/features/library/bulkApi.ts";
import { closeDetailSheet } from "@web/features/detail/sheetUrl.ts";
import { useSheetUrlSync } from "@web/features/detail/useSheetUrlSync.ts";
import { coyLog } from "@web/lib/coyLog.ts";
import { useCoyKoiLiveConsole } from "./useCoyKoiLiveConsole.ts";
import { useGlobeStore } from "@web/stores/globe.ts";

/**
 * Four regions on a black canvas: sidebar, main, sheet, player — each a rounded
 * panel with a gap between them. Current Spotify, not 2019 flush-edge layout.
 */
export function Shell() {
  useCoyKoiLiveConsole();
  useSheetUrlSync();
  const startSync = useStartSync();
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);
  const setSheetOpen = useUiStore((s) => s.setSheetOpen);
  const sheetOpen = useUiStore((s) => s.sheetOpen);
  const setTarget = useDetailStore((s) => s.setTarget);
  const clearSelection = useSelectionStore((s) => s.clear);
  const selectionCount = useSelectionStore((s) => s.count);
  const trailBack = useBrowseTrailStore((s) => s.back);
  const trailDrawer = useBrowseTrailStore((s) => s.drawer);
  const trailStack = useBrowseTrailStore((s) => s.stack);
  const undo = useUndo();
  const redo = useRedo();

  // After Spotify OAuth redirect: toast + auto-pull library into this Clerk user.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const auth = params.get("auth");
    if (!auth) return;
    const message = params.get("message");
    const user = params.get("user");
    window.history.replaceState({}, "", window.location.pathname + window.location.hash);
    if (auth === "ok") {
      coyLog.info(`OAuth OK — connected as ${user ?? "(unknown)"}`);
      toast.success(user ? `Connected as ${user}` : "Connected to Spotify");
      startSync.mutate({ scope: "full" });
    } else {
      coyLog.error(`OAuth failed — ${message ?? "unknown error"}`);
      toast.error(message ?? "Spotify login failed");
    }
    // Intentionally once on mount (OAuth return URL).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- OAuth query consume
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      const typing =
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.tagName === "SELECT" ||
          target.isContentEditable);
      if (typing) return;

      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "z") {
        e.preventDefault();
        if (e.shiftKey) redo.mutate();
        else undo.mutate();
        return;
      }

      if (e.key === "[") {
        e.preventDefault();
        toggleSidebar();
      }
      if (e.key === "]") {
        e.preventDefault();
        if (sheetOpen) {
          closeDetailSheet();
          return;
        }
        const target = useDetailStore.getState().target;
        if (target) {
          const stack = useBrowseTrailStore.getState().stack;
          if (stack.length === 0) {
            useBrowseTrailStore.getState().resetTo({
              type: target.type,
              id: target.id,
              label: target.id,
            });
          }
          setSheetOpen(true);
        }
      }
      if (e.key === "Escape") {
        if (useGlobeStore.getState().layers.cleanStage) {
          e.preventDefault();
          useGlobeStore.getState().exitCleanStage();
          return;
        }
        if (trailDrawer || trailStack.length > 1) {
          e.preventDefault();
          const hadDrawer = trailDrawer !== null;
          trailBack();
          if (hadDrawer) return;
          const top = useBrowseTrailStore.getState().stack.at(-1);
          if (top) {
            setTarget({ type: top.type, id: top.id });
            setSheetOpen(true);
          } else {
            closeDetailSheet();
          }
          return;
        }
        if (sheetOpen) {
          e.preventDefault();
          closeDetailSheet();
          return;
        }
        if (selectionCount() > 0) {
          e.preventDefault();
          clearSelection();
        }
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [
    toggleSidebar,
    setSheetOpen,
    sheetOpen,
    undo,
    redo,
    clearSelection,
    selectionCount,
    trailBack,
    trailDrawer,
    trailStack.length,
    setTarget,
  ]);

  return (
    <div className="flex h-full flex-col gap-2 bg-bg p-2">
      <LibraryDndProvider>
        <div className="flex min-h-0 flex-1 gap-2">
          <Sidebar />
          <main className="relative flex min-w-0 flex-1 flex-col overflow-hidden rounded-lg bg-panel">
            <ConnectionBanner />
            <div className="min-h-0 flex-1 overflow-hidden">
              <Outlet />
            </div>
            <ExploreDrawer />
          </main>
          <DetailSheet />
        </div>
      </LibraryDndProvider>
      <PlayerBar />
    </div>
  );
}
